<template>
  <div ref="image" v-if="shouldRender">
    <picture class="image" v-if="image && image.url && displaySourceSet" :class="{ skeleton: skeleton && !loaded }">
      <source v-if="lazy" :data-srcset="displaySourceSet" :sizes="sizes" />
      <source v-else :srcset="displaySourceSet" :sizes="sizes" />
      <img
        v-if="lazy"
        :data-srcset="displaySourceSet"
        :data-src="fallbackUrl"
        :alt="image ? image.alt : ''"
        class="lazyload image-lazy"
        @load="loaded = true"
      />
      <img v-else :srcset="displaySourceSet" :src="fallbackUrl" :alt="image ? image.alt : ''" @load="loaded = true" />
    </picture>
    <picture class="image" v-else :class="{ skeleton: skeleton && !loaded }">
      <source :data-srcset="sourceSet" />
      <img
        v-if="lazy"
        :data-srcset="sourceSet"
        :data-src="fallbackUrl"
        :alt="image ? image.alt : ''"
        class="lazyload image-lazy"
        @load="loaded = true"
      />
      <img v-else :srcset="sourceSet" :src="fallbackUrl" :alt="image ? image.alt : ''" @load="loaded = true" />
    </picture>
  </div>
</template>
<script>
import { ref, onMounted } from 'vue';
export default {
  props: {
    /**
     * Image object that holds image data
     */
    image: Object,
    /**
     * Lazy load the image or not. Image is not loaded until close to viewport
     */
    lazy: {
      type: Boolean,
      default: true,
    },
    /**
     * List of values for the image to resize into in widths. Work with breakpoints prop
     */
    widths: {
      type: Array,
      default: () => [320, 425, 768, 970, 1300, 1920],
    },
    /**
     * List of values for the image to resize when breakpoint is satisfied. Work with widths prop
     */
    breakpoints: {
      type: Array,
      default: () => [320, 425, 768, 970, 1300, 1920],
    },
    /**
     * Value of quality for the image to load in
     */
    quality: {
      type: Number,
      default: 80,
    },
    skeleton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      webp: false,
    };
  },

  beforeMount() {
    if ('localStorage' in window) {
      this.testWebp();
    }
  },

  setup() {
    let isMobile = ref(false);
    let shouldRender = ref(false);
    let loaded = ref(false);
    onMounted(() => {
      isMobile.value = window.matchMedia('(max-width: 767px)').matches;
      shouldRender.value = window && !window.isServer;
      loaded.value = true;
    });
    return {
      isMobile,
      shouldRender,
      loaded,
    };
  },

  methods: {
    getUrl(width, quality) {
      if (window.devicePixelRatio > 1) {
        if (this.isMobile) {
          quality = Math.round(quality / 2);
        } else {
          quality = this.quality - 20;
        }
      }

      return this.image ? this.image.url.replace('{width}', width).replace('{q}', quality) : null;
    },

    getWebpUrl(width, quality) {
      return this.getUrl(width, Math.min(quality, 100)) + '&fm=webp';
    },

    testWebp() {
      try {
        if (!window.localStorage) {
          return;
        }

        if (window.localStorage.getItem('webp') && !this.shouldUpdateWebp()) {
          this.webp = window.localStorage.getItem('webp') == 'true';
          return;
        }

        let canvas = document.createElement('canvas');

        if (canvas.getContext && canvas.getContext('2d')) {
          this.webp = canvas.toDataURL('image/webp').indexOf('data:image/webp') == 0;
        }

        window.localStorage.setItem('webp', this.webp);
        window.localStorage.setItem('webp-time', new Date().getTime());
      } catch (e) {
        console.warn(e);
      }
    },

    shouldUpdateWebp() {
      try {
        let time = window.localStorage.getItem('webp-time');
        if (!time) {
          return true;
        }

        return new Date().getTime() - window.parseInt(time) >= 86400000;
      } catch (e) {
        console.warn(e);
      }
      return false;
    },
  },

  computed: {
    adjustedWidths() {
      return this.widths.map((width) => Math.round(width * window.devicePixelRatio));
    },

    adjustedBreakpoints() {
      return this.breakpoints.map((breakpoint) => Math.round(breakpoint * window.devicePixelRatio));
    },

    sourceSet() {
      return this.image ? this.image.url1x + ' 1x, ' + this.image.url2x + ' 2x, ' + this.image.url3x + ' 3x' : '';
    },

    displaySourceSet() {
      if (this.webp) {
        return this.adjustedWidths.map((width) => `${this.getWebpUrl(width, this.quality)} ${width}w`).join(', ');
      } else {
        return this.adjustedWidths.map((width) => `${this.getUrl(width, this.quality)} ${width}w`).join(', ');
      }
    },

    sizes() {
      const breakpoints = [...this.adjustedBreakpoints].reverse();
      const widths = [...this.adjustedWidths].reverse();
      return breakpoints.map((breakpoint, index) => `(min-width: ${breakpoint}px) ${widths[index]}px`).join(', ');
    },

    fallbackUrl() {
      if (this.image && this.image.url) {
        return this.image.url.replace('{width}', this.widths[this.widths.length - 1]).replace('{q}', 50);
      }

      return '';
    },
  },
};
</script>
<style lang="postcss" scoped>
.image {
  * {
    width: 100%;
  }

  /* purgecss ignore */
  &:not(.image-lazy) {
    opacity: 1;
  }

  .image-lazy {
    line-height: 1em;
    text-indent: -9999px;
    opacity: 0;

    /* lazyloaded class is added by Lazy.js, it needs to be ignored by PurgeCSS */
    /* purgecss ignore */
    &.lazyloaded {
      opacity: 1;
    }
  }
}
</style>
